import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './OverflowRow.css';

interface OverflowRowProps {
  /** Rendered left of the chips and never collapsed. */
  label?: React.ReactNode;
  /**
   * One entry per chip. `key` must be stable; `active` drives the overflow badge.
   * `menuNode` replaces `node` inside the "+N" menu — an item that is itself a
   * dropdown must render FLAT there, or it becomes a dropdown inside a dropdown
   * whose pop is clipped by the menu's own scroll box.
   */
  items: { key: string; active?: boolean; node: React.ReactNode; menuNode?: React.ReactNode }[];
  className?: string;
  title?: string;
}

/** Must match the `gap` on `.ovr-row`, and the rendered width of the "+N" button. */
const GAP = 8;
const MORE_BUTTON_WIDTH = 56;

/**
 * A single-line chip row that pushes whatever does not fit into a "+N" popover.
 *
 * These rows used to wrap, and every extra line grew the filter header and pushed
 * the map and carousel further down the page — the content people actually came
 * for. Height is now fixed no matter how narrow the window gets.
 *
 * Chips are measured after paint (they are laid out once, off-screen-clipped) and
 * re-measured on container resize, so the cut-off follows the real rendered width
 * rather than an assumed chip size.
 */
export const OverflowRow: React.FC<OverflowRowProps> = ({ label, items, className, title }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLDivElement | null>(null);
  const moreRef = useRef<HTMLButtonElement | null>(null);
  const [visibleCount, setVisibleCount] = useState(items.length);
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);

  const measure = useCallback(() => {
    const container = containerRef.current;
    const measurer = measureRef.current;
    if (!container || !measurer) return;
    const chips = Array.from(measurer.children) as HTMLElement[];
    if (chips.length === 0) return;

    // No layout yet (first paint, a hidden ancestor, or a non-layout environment
    // like jsdom): show everything. Collapsing on a zero measurement would hide
    // every chip behind "+N" for reasons that have nothing to do with width.
    const width = container.clientWidth;
    if (!width) {
      setVisibleCount(items.length);
      return;
    }

    const labelEl = container.querySelector('.ovr-label') as HTMLElement | null;
    const available = width - (labelEl?.offsetWidth ?? 0) - GAP;
    const widths = chips.map((c) => c.offsetWidth + GAP);

    // Everything fits: show it. Reserving room for "+N" unconditionally dropped a
    // chip out of rows that had space for all of them — and because the row is
    // sized by its own content, that fed back through the ResizeObserver and
    // collapsed the row one chip at a time down to "+N".
    if (widths.reduce((a, b) => a + b, 0) <= available) {
      setVisibleCount(items.length);
      return;
    }

    // Only now does the "+N" button need room of its own.
    const budget = available - MORE_BUTTON_WIDTH;
    let used = 0;
    let fit = 0;
    for (const w of widths) {
      if (used + w > budget) break;
      used += w;
      fit += 1;
    }
    // Never collapse to zero chips: one chip plus "+N" reads better than "+N" alone.
    setVisibleCount(Math.max(1, Math.min(fit, items.length)));
  }, [items.length]);

  useLayoutEffect(measure, [measure, items]);

  // Anchor the portalled menu to the button, and keep it there while scrolling.
  const placeMenu = useCallback(() => {
    const btn = moreRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    setMenuPos({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
  }, []);

  useLayoutEffect(() => {
    if (open) placeMenu();
  }, [open, placeMenu]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => ro.disconnect();
  }, [measure]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (containerRef.current?.contains(t)) return;
      if ((t as HTMLElement).closest?.('.ovr-menu')) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', placeMenu);
    window.addEventListener('scroll', placeMenu, true);
    return () => {
      document.removeEventListener('pointerdown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', placeMenu);
      window.removeEventListener('scroll', placeMenu, true);
    };
  }, [open, placeMenu]);

  const hidden = items.slice(visibleCount);
  const hiddenActive = hidden.filter((i) => i.active).length;

  return (
    <div className={`ovr-row${className ? ` ${className}` : ''}`} ref={containerRef} title={title}>
      {label && <span className="ovr-label">{label}</span>}

      {/* Off-layout copy used purely for width measurement, so the visible row can
          stay clipped to one line without hiding what we need to measure. */}
      <div className="ovr-measure" ref={measureRef} aria-hidden="true">
        {items.map((it) => (
          <span key={`m:${it.key}`}>{it.node}</span>
        ))}
      </div>

      {items.slice(0, visibleCount).map((it) => (
        <React.Fragment key={it.key}>{it.node}</React.Fragment>
      ))}

      {hidden.length > 0 && (
        <button
          type="button"
          ref={moreRef}
          className={`ovr-more${hiddenActive > 0 ? ' has-active' : ''}`}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          title={`${hidden.length} more`}
        >
          +{hidden.length}
          {hiddenActive > 0 && <span className="ovr-more-dot" />}
        </button>
      )}

      {/* Portalled to the body: this row is overflow:hidden (that is what keeps it
          to one line), which would otherwise clip the menu into invisibility. */}
      {open && hidden.length > 0 && menuPos && createPortal(
        <div
          className="ovr-menu"
          style={{ top: menuPos.top, right: menuPos.right }}
          role="listbox"
        >
          {hidden.map((it) => (
            <div
              className={`ovr-menu-item${it.menuNode ? ' ovr-menu-section' : ''}`}
              key={`h:${it.key}`}
            >
              {it.menuNode ?? it.node}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
};
